export type AppCopy = {
  estimateDisclaimer: string;
  zipSectionTitle: string;
  zipTitle: string;
  zipDescription: string;
  homeSectionTitle: string;
  currentSystemSectionTitle: string;
  desiredSystemSectionTitle: string;
  contactTitle: string;
  contactCta: string;
  resultsEyebrow: string;
  resultsTitle: string;
  resultsDescription: string;
  resultsSubmitCta: string;
  resultsRestartCta: string;
};

export const DEFAULT_APP_COPY: AppCopy = {
  estimateDisclaimer:
    "The pricing provided through this tool is a general estimate based on the information entered and typical system configurations. It is not a final quote or binding offer. Actual pricing may vary and is subject to an in-home evaluation by a licensed Baker Home Energy professional, where additional factors such as system sizing, ductwork, electrical requirements, structural conditions, and installation complexities will be assessed. Final pricing will be confirmed following a full consultation.",
  zipSectionTitle: "Service Area Check",
  zipTitle: "What is your ZIP code?",
  zipDescription: "Enter your ZIP code to ensure your home is in our service area",
  homeSectionTitle: "Tell us about your current home",
  currentSystemSectionTitle: "Tell us about your current system",
  desiredSystemSectionTitle: "Tell us about your desired system",
  contactTitle: "What is the best contact information for us to discuss your new system?",
  contactCta: "Click to See Your Estimate",
  resultsEyebrow: "Your Estimate Is Ready",
  resultsTitle: "Pick the option that fits your home",
  resultsDescription: "You can choose the best fit for your budget and needs.",
  resultsSubmitCta: "Finalize estimate",
  resultsRestartCta: "Start another estimate",
};

function normalizeKey(value: unknown) {
  return String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function getValue(row: Record<string, any>, aliases: string[]) {
  const keys = Object.keys(row);
  for (const alias of aliases) {
    const match = keys.find((key) => normalizeKey(key) === normalizeKey(alias));
    if (match) return String(row[match] ?? "").trim();
  }
  return "";
}

function getCopyOverride(rows: Record<string, any>[], key: keyof AppCopy) {
  const row = rows.find(
    (item) => normalizeKey(getValue(item, ["Field Name", "Key"])) === normalizeKey(key),
  );
  if (!row) return "";
  return getValue(row, ["Copy", "Primary Prompt", "Value"]);
}

export function resolveAppCopy(rows: Record<string, any>[] = []): AppCopy {
  if (!Array.isArray(rows) || rows.length === 0) return DEFAULT_APP_COPY;

  const resolved = { ...DEFAULT_APP_COPY };
  (Object.keys(DEFAULT_APP_COPY) as Array<keyof AppCopy>).forEach((key) => {
    const override = getCopyOverride(rows, key);
    if (override) resolved[key] = override;
  });

  const sheetDisclaimer = rows
    .map((row) => getValue(row, ["Disclaimer Copy"]))
    .find(Boolean);
  if (sheetDisclaimer) resolved.estimateDisclaimer = sheetDisclaimer;

  return resolved;
}
